import React, { useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import Loader from "./Loader";

const ImageCropper = ({
  open,
  image,
  onCrop,
  onCancel,
  loading,
  aspectRatio,
  error,
}) => {
  const cropperRef = useRef(null);
  const croppingRef = useRef(false);
  const cropOperationRef = useRef(0);

  useEffect(() => {
    cropOperationRef.current += 1;
    croppingRef.current = false;
  }, [image]);

  useEffect(
    () => () => {
      cropOperationRef.current += 1;
    },
    [],
  );

  const handleCrop = () => {
    if (croppingRef.current) return;

    const cropper = cropperRef.current?.cropper;

    if (!cropper) return;

    croppingRef.current = true;
    const operation = ++cropOperationRef.current;

    try {
      // Limit the output so large source images cannot allocate huge canvases.
      const canvas = cropper.getCroppedCanvas({
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
        maxWidth: 2048,
        maxHeight: 2048,
      });

      if (!canvas) {
        croppingRef.current = false;
        return;
      }

      canvas.toBlob(
        (blob) => {
          if (operation !== cropOperationRef.current) return;
          croppingRef.current = false;
          if (blob) {
            const file = new File([blob], "croppedImage.jpg", {
              type: "image/jpeg",
            });
            onCrop(file);
          }
        },
        "image/jpeg",
        0.9, // 90% quality
      );
    } catch {
      croppingRef.current = false;
    }
  };

  const handleCancel = () => {
    cropOperationRef.current += 1;
    croppingRef.current = false;
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm">
      {loading && <Loader />}
      <DialogTitle>Crop Image</DialogTitle>
      <DialogContent>
        {error && (
          <Typography role="alert" color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        {/* TODO: upgrade or replace the unmaintained react-cropper wrapper. */}
        <Cropper
          ref={cropperRef}
          className="fix-on-mob"
          style={{ height: 370, width: 360 }}
          src={image}
          viewMode={1}
          autoCropArea={1}
          background={false}
          guides={true}
          aspectRatio={aspectRatio || 3 / 4}
          zoomOnWheel={false}
          minCropBoxHeight={10}
          minCropBoxWidth={10}
          responsive={true}
          checkOrientation={false}
        />
      </DialogContent>
      <DialogActions>
        <Button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handleCancel();
          }}
          color="secondary"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handleCrop();
          }}
          color="primary"
          disabled={loading}
        >
          Crop
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropper;
